import { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const MEETING_TYPES = [
  { value: 'monthly', label: 'මාසික මහසභා රැස්වීම' },
  { value: 'anniversary', label: 'සංවත්සර මහසභා රැස්වීම' },
  { value: 'special', label: 'විශේෂ මහසභා රැස්වීම' },
];

const ATTENDANCE = [
  { value: 'present', label: 'Present' },
  { value: 'absent', label: 'Absent' },
];

export default function MeetingsOverview({ shops, members }) {
  const [form, setForm] = useState({
    meeting_type: '',
    member_id: '',
    meeting_date: '',
    attendance: '',
  });
  const [saving, setSaving] = useState(false);
  const [memberList, setMemberList] = useState([]);
  const [records, setRecords] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(false);

  // Load all members
  useEffect(() => {
    const loadMembers = async () => {
      try {
        const results = await Promise.all(
          shops.map(s => api.get(`/members/shop/${s.id}`).catch(() => null))
        );
        const memberData = results
          .filter(r => r?.data)
          .map(r => r.data);
        setMemberList(memberData);
      } catch (err) {
        console.error('Failed to load members');
      }
    };
    if (shops.length) loadMembers();
  }, [shops]);

  // Load meeting records
  useEffect(() => {
    const loadRecords = async () => {
      setLoadingRecords(true);
      try {
        const { data } = await api.get('/meetings');
        setRecords(data || []);
      } catch (err) {
        console.error('Failed to load meetings');
      } finally {
        setLoadingRecords(false);
      }
    };
    loadRecords();
  }, []);

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.meeting_type || !form.member_id || !form.meeting_date || !form.attendance) {
      toast.error('Please fill all fields');
      return;
    }
    setSaving(true);
    try {
      const response = await api.post('/meetings', {
        member_id: form.member_id,
        meeting_type: form.meeting_type,
        meeting_date: form.meeting_date,
        attendance: form.attendance,
      });
      toast.success('Meeting recorded');
      setForm({ meeting_type: '', member_id: '', meeting_date: '', attendance: '' });
      setRecords([response.data, ...records]);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const getMemberName = id => {
    const m = memberList.find(mem => mem.id === Number(id));
    return m ? `${m.name} / ${m.shop_code || ''}` : '—';
  };

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      {/* Form */}
      <div className="card" style={{ padding: 24 }}>
        <h3 style={{ marginBottom: 18 }}>Record Meeting</h3>
        <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 18 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
            <div className="form-group">
              <label className="form-label">Meeting Type</label>
              <select
                className="form-select"
                value={form.meeting_type}
                onChange={e => setForm(p => ({ ...p, meeting_type: e.target.value }))}
                required
              >
                <option value="">— Select Type —</option>
                {MEETING_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Member / Shop</label>
              <select
                className="form-select"
                value={form.member_id}
                onChange={e => setForm(p => ({ ...p, member_id: e.target.value }))}
                required
              >
                <option value="">— Select Member —</option>
                {memberList.map(m => (
                  <option key={m.id} value={m.id}>
                    {m.name} / {m.shop_code}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
            <div className="form-group">
              <label className="form-label">Meeting Date</label>
              <input
                type="date"
                className="form-input"
                value={form.meeting_date}
                onChange={e => setForm(p => ({ ...p, meeting_date: e.target.value }))}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Attendance</label>
              <select
                className="form-select"
                value={form.attendance}
                onChange={e => setForm(p => ({ ...p, attendance: e.target.value }))}
                required
              >
                <option value="">— Select —</option>
                {ATTENDANCE.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
              </select>
            </div>
          </div>

          <button className="btn btn-primary" type="submit" disabled={saving}>
            {saving ? 'Saving…' : 'Save Meeting'}
          </button>
        </form>
      </div>

      {/* Records */}
      <div className="card" style={{ overflow: 'hidden' }}>
        <div style={{ padding: 24, borderBottom: '1px solid var(--surface2)' }}>
          <h3 style={{ margin: 0 }}>Recent Meetings</h3>
        </div>
        <div style={{ overflowY: 'auto', maxHeight: 400 }}>
          {loadingRecords ? (
            <p style={{ padding: 24, textAlign: 'center', color: 'var(--text3)' }}>Loading…</p>
          ) : records.length === 0 ? (
            <p style={{ padding: 24, textAlign: 'center', color: 'var(--text3)' }}>No meetings recorded yet.</p>
          ) : (
            <table style={{ width: '100%' }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                <tr>
                  <th>Type</th>
                  <th>Member / Shop</th>
                  <th>Date</th>
                  <th>Attendance</th>
                </tr>
              </thead>
              <tbody>
                {records.map(record => (
                  <tr key={record.id}>
                    <td><span className="badge badge-blue" style={{ textTransform: 'capitalize' }}>{record.meeting_type}</span></td>
                    <td>{getMemberName(record.member_id)}</td>
                    <td style={{ fontSize: 12, fontFamily: 'DM Mono, monospace' }}>{new Date(record.meeting_date).toLocaleDateString()}</td>
                    <td><span className={record.attendance === 'present' ? 'badge badge-green' : 'badge' }>{record.attendance}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
